"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, FileCheck2, Headphones } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface GuaranteeItem {
  id: string;
  title: string;
  tag: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const GUARANTEES: GuaranteeItem[] = [{
  id: "catalog",
  title: "TIERED TOOL SELECTION",
  tag: "SPEC 01",
  description: "33 workshop-grade items across 11 core categories, from daily service essentials to high-duty fabrication gear.",
  icon: Wrench
}, {
  id: "checkout",
  title: "SECURE CHECKOUT",
  tag: "SPEC 02",
  description: "Encrypted payment pipelines with clear transaction receipts and reliable nationwide shipping across the U.S.",
  icon: ShieldCheck
}, {
  id: "specs",
  title: "VERIFIED SPECIFICATIONS",
  tag: "SPEC 03",
  description: "Real physical dimensions, duty cycles, and electrical ratings listed upfront on every machine and hand tool.",
  icon: FileCheck2
}, {
  id: "support",
  title: "DIRECT TECHNICAL SUPPORT",
  tag: "SPEC 04",
  description: "Responsive help from knowledgeable staff for tooling compatibility, order tracking, and equipment maintenance.",
  icon: Headphones
}];
export default function Guarantees() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.24,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <section id="why-shop-with-us" data-nav="dark" className="bg-background text-foreground py-16 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block with Precision Spec Rule */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="font-mono text-xs uppercase tracking-widest border-border bg-card/60 text-muted-foreground rounded-xs px-2 py-0.5">
              OPERATIONAL GUARANTEES
            </Badge>
          </div>

          <div className="border-l-[3px] border-primary pl-4 md:pl-5">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight uppercase leading-none text-foreground">
              BUILT FOR WORKSHOP RELIABILITY
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Four operational standards that back every tool, order, and delivery across the country.
            </p>
          </div>
        </div>

        {/* 4-Column Feature Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {GUARANTEES.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.id} variants={itemVariants} data-index={index} className="h-full">
                <Card className="group relative h-full flex flex-col justify-between rounded-xs bg-card border border-border p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md">
                  <div>
                    <CardHeader className="p-0 mb-5 flex flex-row items-center justify-between space-y-0">
                      <div className="flex items-center justify-center size-12 rounded-xs bg-muted/70 border border-border text-primary group-hover:border-primary/40 transition-colors">
                        <Icon className="size-6 stroke-[2px]" />
                      </div>
                      <span className="font-mono text-xs font-bold tracking-wider text-muted-foreground">
                        {item.tag}
                      </span>
                    </CardHeader>

                    <CardContent className="p-0">
                      <CardTitle className="font-serif text-lg font-bold tracking-wide uppercase text-card-foreground mb-2.5">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  {/* Industrial bottom accent bar */}
                  <div className="mt-6 pt-2">
                    <div className="h-[2px] w-full bg-border/60 group-hover:bg-primary transition-colors duration-200" />
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}